import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import api from "@/components/customAxios/Axios";
import { useToast } from "../toast";

const MAX_COUNT = 5;

interface MyObjectType {
	jd: string | null;
	role: string | null;
	job_id: number | null;
}

const MultipleFileUpload = ({ setCallFunction }: { setCallFunction: React.Dispatch<React.SetStateAction<boolean>> }) => {
	const uploadTypeRef = useRef<"files" | "folder">("files"); // Used to send correct flag

	const toast = useToast();
	const fileInputRef = useRef<HTMLInputElement>(null);
	const folderInputRef = useRef<HTMLInputElement>(null);

	const { id } = useParams<{ id: string }>();
	const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
	const [fileLimit, setFileLimit] = useState<boolean>(false);
	const [submitting, setSubmitting] = useState<boolean>(false);
	const [jobDetails, setJobDetails] = useState<MyObjectType | null>(null);

	const role = localStorage.getItem("role") || "";
	const isPaidUser = role !== "guest";

	const filterPDFFiles = (files: File[]): File[] => {
		return files.filter(file => file.type === "application/pdf");
	};

	const handleUploadFiles = (files: File[]) => {
		const pdfFiles = filterPDFFiles(files);

		if (pdfFiles.length === 0) {
			toast.error({
				type: "background",
				duration: 5000,
				status: "error",
				title: "Invalid File Type",
				description: "Please select PDF files only.",
				open: true,
			});
			return;
		}

		let newFiles = [...uploadedFiles];

		pdfFiles.forEach((file) => {
			if (!newFiles.find((f) => f.name === file.name)) {
				newFiles.push(file);
			}
		});


		setUploadedFiles(newFiles);
	};

	const handleFileEvent = (e: React.ChangeEvent<HTMLInputElement>) => {
		uploadTypeRef.current = "files";
		const chosenFiles = Array.from(e.target.files || []);
		handleUploadFiles(chosenFiles);
		if (e.target) e.target.value = '';
	};

	const handleFolderEvent = (e: React.ChangeEvent<HTMLInputElement>) => {
		uploadTypeRef.current = "folder";
		const chosenFiles = Array.from(e.target.files || []);
		handleUploadFiles(chosenFiles);
		if (e.target) e.target.value = '';
	};

	useEffect(() => {
		const getJobDetails = async () => {
			try {
				const response = await api.get(`/jobs/${id}`);
				const { role, jd, job_id } = response.data.job_details;
				setJobDetails({ role, jd, job_id });
				localStorage.setItem("job_id", job_id.toString());
			} catch (error: any) {
				toast.error({
					type: "background",
					duration: 5000,
					status: "error",
					title: "Error Fetching Job Details",
					description: error.response?.data?.error || error.response?.data?.message || "Failed to fetch job details. Please try again.",
					open: true,
				});
			}
		};

		getJobDetails();
	}, [id, toast]);
	const handleSubmitFiles = async () => {
		setSubmitting(true);
		try {
			const formData = new FormData();
			uploadedFiles.forEach((file) => formData.append("resume", file));
			if (jobDetails) {
				formData.append("id", jobDetails.job_id!.toString());
				formData.append("role", jobDetails.role!);

			}
			formData.append("upload_type", uploadTypeRef.current);

			const response = await api.post("/upload_resume_to_job", formData);

			if (
				response.status === 200
			) {
				setCallFunction((prev) => !prev)
				toast.success({
					type: "background",
					duration: 3000,
					status: "success",
					title: "Resume uploaded successfully",
					description: `${uploadedFiles.length} PDF file(s) uploaded successfully.`,
					open: true,
				});
				setUploadedFiles([]);
				setFileLimit(false);
			} else {
				throw new Error("Unexpected response format");
			}
		} catch (error: any) {
			console.error("Error uploading resume:", error);
			toast.error({
				type: "background",
				duration: 5000,
				status: "error",
				title: "Error Uploading Resume",
				description: error.response?.data?.error || error.response?.data?.message || "Failed to upload resume. Please try again.",
				open: true,
			});
		} finally {
			setSubmitting(false);
			setUploadedFiles([]);
			setFileLimit(false);
		}
	};

	useEffect(() => {

		if (!isPaidUser && uploadedFiles.length > MAX_COUNT) {
			toast.error({
				type: "background",
				duration: 5000,
				status: "error",
				title: "File Limit Exceeded",
				description: `Free trial users can upload a maximum of ${MAX_COUNT} PDF files.`,
				open: true,
			});
			return
		}


		if (uploadedFiles.length > 0) {
			toast.success({
				type: "background",
				duration: 3000,
				status: "success",
				title: "Files are getting uploaded",
				description: `You have selected ${uploadedFiles.length} PDF file(s)`,
				open: true,
			});
			handleSubmitFiles();
		}

	}, [uploadedFiles]);

	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.ctrlKey && e.key === "u") {
				e.preventDefault();
				if (fileInputRef.current && (!fileLimit || isPaidUser) && !submitting) {
					uploadTypeRef.current = "files";
					fileInputRef.current.click();
				}
			}
			if (e.ctrlKey && e.shiftKey && e.key === "U") {
				e.preventDefault();
				if (isPaidUser && folderInputRef.current && !submitting) {
					uploadTypeRef.current = "folder";
					folderInputRef.current.click();
				}
			}
		};

		window.addEventListener("keydown", handleKeyDown);
		return () => {
			window.removeEventListener("keydown", handleKeyDown);
		};
	}, [fileLimit, submitting, isPaidUser]);

	const triggerFileUpload = () => {
		if (fileInputRef.current && (!fileLimit || isPaidUser) && !submitting) {
			uploadTypeRef.current = "files";
			fileInputRef.current.click();
		}
	};

	const triggerFolderUpload = () => {
		if (isPaidUser && folderInputRef.current && !submitting) {
			uploadTypeRef.current = "folder";
			folderInputRef.current.click();
		}
	};

	return (
		<div>
			{/* File input for individual files */}
			<input
				ref={fileInputRef}
				id="fileUpload"
				type="file"
				multiple
				accept="application/pdf"
				onChange={handleFileEvent}
				className="hidden"
				disabled={!isPaidUser && (fileLimit || submitting)}
			/>

			{/* Folder input for paid users only */}
			{isPaidUser && (
				<input
					ref={folderInputRef}
					id="folderUpload"
					type="file"
					onChange={handleFolderEvent}
					className="hidden"
					{...({ webkitdirectory: "" } as any)}
					disabled={submitting}
				/>
			)}

			{!uploadedFiles.length && (
				<div className="flex gap-3">
					{/* File upload button */}
					<div className="relative group/{file}">
						<button
							onClick={triggerFileUpload}
							disabled={!isPaidUser && (fileLimit || submitting)}
							className={`flex items-center justify-center rounded ${!isPaidUser && (fileLimit || submitting)
								? "opacity-50 cursor-not-allowed"
								: "cursor-pointer"
								}`}
						>
							<svg
								width="28"
								height="18"
								viewBox="0 0 18 18"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path d="M1 9H17" stroke="black" strokeWidth="1.5" strokeLinecap="round" />
								<path d="M9 17L9 1" stroke="black" strokeWidth="1.5" strokeLinecap="round" />
							</svg>
						</button>
						<p className="absolute w-[200px] text-center ml-2 px-2 py-1 text-sm bg-black text-white rounded -z-10 group-hover/{file}:z-50 transition duration-300 ease-in-out">
							Add a candidate for Screening. <br /> CTRL + U
						</p>
					</div>
					{/* Folder upload button for paid users */}
					{isPaidUser && (
						<div className="relative group/{folder}">
							<button
								onClick={triggerFolderUpload}
								disabled={submitting}
								className={`flex items-center justify-center rounded ${submitting ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
									}`}

							>
								<svg
									width="28"
									height="20"
									viewBox="0 0 18 18"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										d="M2 4a1 1 0 011-1h3.5a1 1 0 01.8.4l1.4 1.8a1 1 0 00.8.4H15a1 1 0 011 1v8a1 1 0 01-1 1H3a1 1 0 01-1-1V4z"
										stroke="black"
										strokeWidth="1.5"
										fill="none"
									/>
									<path d="M6 9H12" stroke="black" strokeWidth="1.5" strokeLinecap="round" />
									<path d="M9 12L9 6" stroke="black" strokeWidth="1.5" strokeLinecap="round" />
								</svg>
							</button>
							<p className="absolute w-[200px] text-center ml-2 px-2 py-1 text-sm bg-black text-white rounded -z-10 group-hover/{folder}:z-50 transition duration-300 ease-in-out">
								Add folder for Screening. <br /> CTRL + Shift + U
							</p>
						</div>
					)}
				</div>
			)}


			{/* Display current status */}
			{submitting && (
				<div className="mt-2 text-sm text-gray-600">
					Uploading {uploadedFiles.length} file(s)...
				</div>
			)}
		</div>
	);
};

export default MultipleFileUpload;